<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from 'vue'

import LoadingState from './components/LoadingState.vue'
import ToastStack from './components/ToastStack.vue'
import { navigate, useRouter } from './router'
import { bootstrapSession, useSession } from './session'
import AdminView from './views/AdminView.vue'
import CreateView from './views/CreateView.vue'
import EditorView from './views/EditorView.vue'
import LibraryView from './views/LibraryView.vue'
import LoginView from './views/LoginView.vue'
import NotFoundView from './views/NotFoundView.vue'
import PublicDeckView from './views/PublicDeckView.vue'
import RemoteView from './views/RemoteView.vue'
import ShowView from './views/ShowView.vue'

const router = useRouter()
const session = useSession()
const dark = ref(false)
const directPresentationHandled = ref(false)

const publicRoute = computed(() =>
  ['public', 'show', 'remote', 'not-found'].includes(router.route.value.name),
)
const waitingForSession = computed(() =>
  !publicRoute.value
  && router.route.value.name !== 'login'
  && !session.state.ready,
)

function applyTheme(value: boolean): void {
  dark.value = value
  document.documentElement.dataset.theme = value ? 'dark' : 'light'
  document.documentElement.style.colorScheme = value ? 'dark' : 'light'
  document.querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', value ? '#111311' : '#f4f4f0')
}

function toggleTheme(): void {
  applyTheme(!dark.value)
  localStorage.setItem('getslides:theme', dark.value ? 'dark' : 'light')
}

watchEffect(() => {
  if (!session.state.ready) return
  const route = router.route.value
  if (route.name === 'login' && session.state.authenticated) {
    navigate(session.state.kind === 'admin' ? '/admin' : '/', true)
    return
  }
  if (['home', 'new', 'editor'].includes(route.name)) {
    if (session.state.kind === 'admin') {
      navigate('/admin', true)
    } else if (!session.state.authenticated) {
      navigate('/login', true)
    }
  }
  if (
    !directPresentationHandled.value
    && route.name === 'home'
    && session.state.kind === 'user'
  ) {
    const presentationId = new URLSearchParams(window.location.search).get('presentation') ?? ''
    directPresentationHandled.value = true
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(presentationId)) {
      navigate(`/deck/${presentationId}`, true)
    }
  }
})

onMounted(() => {
  const preference = localStorage.getItem('getslides:theme')
  applyTheme(
    preference
      ? preference === 'dark'
      : window.matchMedia('(prefers-color-scheme: dark)').matches,
  )
  void bootstrapSession()
})
</script>

<template>
  <LoadingState v-if="waitingForSession" title="Открываем GetSlides" full />

  <LoginView v-else-if="router.route.value.name === 'login'" />
  <LibraryView
    v-else-if="router.route.value.name === 'home'"
    @toggle-theme="toggleTheme"
  />
  <CreateView
    v-else-if="router.route.value.name === 'new'"
    @toggle-theme="toggleTheme"
  />
  <EditorView
    v-else-if="router.route.value.name === 'editor'"
    :id="router.route.value.id"
    @toggle-theme="toggleTheme"
  />
  <PublicDeckView
    v-else-if="router.route.value.name === 'public'"
    :slug="router.route.value.slug"
  />
  <ShowView
    v-else-if="router.route.value.name === 'show'"
    :slug="router.route.value.slug"
  />
  <RemoteView
    v-else-if="router.route.value.name === 'remote'"
    :slug="router.route.value.slug"
  />
  <AdminView
    v-else-if="router.route.value.name === 'admin'"
    @toggle-theme="toggleTheme"
  />
  <NotFoundView v-else />

  <ToastStack />
</template>
